import { AxiosResponse } from "axios";
import { api } from ".";
import { ICmTableDataMediaItem, ICmTableDataRelation, INormalisedRelations, PrimaryImage, RelationshipEditorState } from "../types";

export interface ICmTableFieldSchemaItem {
  type: string;
  label: string;
  choices?: string[];
}

export interface ICmTableFieldSchema {
  [fieldKey: string]: ICmTableFieldSchemaItem;
}

export interface ICmTableMapDisplayConfig {
  icon?: string;
  color?: string;
}

export type CmTableSpatialType = "point" | "polygon" | "linestring" | "none";

/**
 * Raw spatial type as it may arrive from the backend API (e.g. "multipolygon",
 * "multilinestring") or config. "linestring" is the canonical line type —
 * "line" is no longer accepted. Use `normaliseSpatialType()` to collapse
 * multi-variants into the base `CmTableSpatialType` before passing to UI
 * components like DrawingTools.
 */
export type CmTableSpatialTypeRaw = CmTableSpatialType | "multipoint" | "multipolygon" | "multilinestring";

/**
 * Collapses multi-variants into the base spatial type used by DrawingTools,
 * CmTableDrawMap, and map layer specs.
 * - multipolygon     → polygon
 * - multilinestring  → linestring
 * - multipoint       → point
 * - unknown/undefined → none
 */
export function normaliseSpatialType(raw: CmTableSpatialTypeRaw | string | null | undefined): CmTableSpatialType {
  switch (raw) {
    case "polygon":
    case "multipolygon":
      return "polygon";
    case "linestring":
    case "multilinestring":
      return "linestring";
    case "point":
    case "multipoint":
      return "point";
    default:
      return "none";
  }
}

// ─── CM Table Data (row list) ──────────────────────────────────────────────

export interface ICmTableDataRelations {
  related_entities: Record<string, ICmTableDataRelation[]>;
  geo_entities: Record<string, ICmTableDataRelation[]>;
  media: ICmTableDataMediaItem[];
}

export function normaliseRelations(raw: any | undefined): INormalisedRelations {
  if (!raw) return { byTableSlug: {}, media: [] };

  // Idempotency: already normalised — return as-is
  if (raw.byTableSlug) return raw;

  const byTableSlug: Record<string, ICmTableDataRelation[]> = {};

  // V2 Shape B: relationships keyed directly by table slug at the top level.
  // e.g. { "media": [...], "org999_culsites": [...] }
  // Detected by the absence of both "related_entities" and "geo_entities".
  const isV2FlatShape = raw.related_entities === undefined && raw.geo_entities === undefined;

  if (isV2FlatShape) {
    // The API uses anchor-type names as keys (e.g. "spatialdataset") but the
    // relationship editor and column builder use table slugs (e.g. "spatial-datasets").
    // Map known anchor-type keys to their canonical table slug.
    const anchorTypeToTableSlug: Record<string, string> = { spatialdataset: "spatial-datasets" };

    for (const [key, items] of Object.entries(raw)) {
      if (key === "media" || !Array.isArray(items)) continue;
      const slug = anchorTypeToTableSlug[key] ?? key;
      byTableSlug[slug] = items as ICmTableDataRelation[];
    }
    return {
      byTableSlug,
      media: Array.isArray(raw.media) ? raw.media : []
    };
  }

  // V1 Shape: related_entities and geo_entities buckets keyed by model/class name.
  const buckets = [raw.related_entities, raw.geo_entities];
  for (const bucket of buckets) {
    if (!bucket || typeof bucket !== "object" || Array.isArray(bucket)) continue;
    for (const [modelKey, items] of Object.entries(bucket)) {
      if (!Array.isArray(items)) continue;
      for (const item of items) {
        // Prefer table_slug from item; fall back to the model key for pre-update rows
        const slug = (item as any).table_slug ?? modelKey;
        (byTableSlug[slug] ??= []).push(item);
      }
    }
  }

  return {
    byTableSlug,
    media: Array.isArray(raw.media) ? raw.media : []
  };
}

/**
 * Canonical adapter that converts backend wire-format row data into a stable internal shape.
 * Single boundary for all V2 row transformations: field promotion, primary_image normalisation,
 * relationships normalisation, and primary_image enrichment from media.
 */
export function normaliseCmTableRow(row: any): ICmTableDataRow {
  if (!row) return row;

  // Resolve primary image — prefer primary_image, fall back to thumbnail
  const rawImage = row.primary_image ?? row.thumbnail ?? row.image ?? null;
  const imageUrl = typeof rawImage === "string" ? rawImage : (rawImage?.url ?? null);

  // Accept both field names for backward compatibility with cached pre-migration rows
  const rawRelationships = row.relationships ?? row.relations;
  const normalisedRelationships = normaliseRelations(rawRelationships);

  // Build canonical primary_image, then enrich id/name/description from the is_primary media item
  let primaryImage: any = imageUrl ? { id: null, url: imageUrl, name: "", description: "" } : null;
  if (primaryImage) {
    const match = normalisedRelationships.media.find((m: any) => m.is_primary === true);
    if (match) {
      primaryImage = {
        ...primaryImage,
        id: match.id,
        name: match.name || "",
        description: match.entity_description || ""
      };
    }
  }

  return {
    ...row,
    ...(row.row_data ?? {}),
    primary_image: primaryImage,
    thumbnail: imageUrl,
    relationships: normalisedRelationships,
    relations: normalisedRelationships
  };
}

export interface ICmTableDataRow {
  id: number;
  uuid: string;
  name: string;
  description: string;
  table: number;
  table_name: string;
  table_slug: string;
  row_data: Record<string, any>;
  geometry: object | null;
  primary_image: PrimaryImage | null;
  thumbnail: string | null;
  organisation: number;
  user: number;
  restrictions: any[];
  created_at: string;
  last_updated_at: string;
  relations?: INormalisedRelations;
  relationships?: INormalisedRelations; // Normalized V2 shape
}

export interface ICmTableDataResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ICmTableDataRow[];
}

/**
 * Fetches rows for a CM Table identified by its slug.
 */
export const getCmTableData = async (
  tableSlug: string,
  params?: { page?: number; page_size?: number; search?: string; include_relations?: boolean; bbox?: string }
): Promise<AxiosResponse<ICmTableDataResponse>> => {
  const response = await api.get(`v2/tables/${tableSlug}/rows/`, {
    params: {
      // Default false — list calls don't need relationship data; only detail calls do.
      ...(params?.include_relations ? { include_relations: true } : {}),
      ...(params?.page ? { page: params.page } : {}),
      ...(params?.page_size ? { page_size: params.page_size } : {}),
      ...(params?.search ? { search: params.search } : {}),
      ...(params?.bbox ? { bbox: params.bbox } : {})
    }
  });

  // Apply canonical normalization to all rows
  if (response.data?.results) {
    response.data.results = response.data.results.map((row: any) => normaliseCmTableRow(row));
  }

  return response;
};

/**
 * Fetches a single row from a CM Table.
 * Endpoint: GET /api/v2/tables/{slug}/rows/{id}/
 */
export const getCmTableRow = async (
  tableSlug: string,
  rowId: string | number,
  params?: { include_relations?: boolean }
): Promise<AxiosResponse<ICmTableDataRow>> => {
  const response = await api.get(`v2/tables/${tableSlug}/rows/${rowId}/`, {
    params: {
      include_relations: params?.include_relations ?? true
    }
  });

  if (response.data) {
    response.data = normaliseCmTableRow(response.data);
  }

  return response;
};

// ─── CM Table GeoJSON ──────────────────────────────────────────────────────

export interface ICmTableGeoFeature {
  type: "Feature";
  geometry: {
    type: string;
    coordinates: any;
  };
  properties: {
    id: number;
    name: string;
    description: string;
    thumbnail: string | null;
    [key: string]: any;
  };
}

export interface ICmTableGeoResponse {
  type: "FeatureCollection";
  table_id: number;
  table_name: string;
  table_slug: string;
  spatial_type: CmTableSpatialType;
  count: number;
  features: ICmTableGeoFeature[];
}

// ─── Anchor Relationships ─────────────────────────────────────────────────────

export type AnchorType = "cmtabledata" | "media" | "spatialdataset";

export interface IAnchorRelationshipItem {
  to_type: AnchorType;
  to_object_id: number;
}

export interface IAnchorRelationshipsResponse {
  count: number;
  relationships: Array<{
    relationship_id: number;
    from_content_type_model: AnchorType;
    from_object_id: number;
    to_content_type_model: AnchorType;
    to_object_id: number;
    is_primary: boolean;
    description: string;
    related_object: {
      id: number;
      name: string;
      description: string;
      model: AnchorType;
      is_media: boolean;
      media_type?: string;
      table_slug?: string;
      table_name?: string;
    };
  }>;
}

export interface IAnchorRelationshipsPatchPayload {
  /** New relationships to create — `to_object_id` of the row/media being linked. */
  create: IAnchorRelationshipItem[];
  /** `relationship_id`s of existing relationships to remove. */
  delete: number[];
}

/**
 * Diff-applies relationship changes for an anchor — only the additions and
 * removals are sent, unlike a full replace.
 * Endpoint: PATCH /api/v2/anchor-relationships/{type}/{id}/
 */
export const patchAnchorRelationships = async (
  anchorType: AnchorType,
  anchorId: number | string,
  payload: IAnchorRelationshipsPatchPayload
): Promise<AxiosResponse<IAnchorRelationshipsResponse>> => {
  return api.patch(`v2/anchor-relationships/${anchorType}/${anchorId}/`, payload);
};

/**
 * Diffs an "original" relationship snapshot against the current editor state and
 * builds the `{ create, delete }` payload for `patchAnchorRelationships`.
 *
 * - `create`: items in `current` without a `relationship_id` (freshly selected —
 *   see `RelationshipEditorTable`, which seeds new selections with `relationship_id: null`).
 * - `delete`: `relationship_id`s present in `original` but no longer present in `current`.
 */
export const buildAnchorRelationshipsPatchPayload = (
  original: RelationshipEditorState,
  current: RelationshipEditorState
): IAnchorRelationshipsPatchPayload => {
  const toAnchorType = (slug: string): AnchorType =>
    slug === "media" ? "media" : slug === "spatial-datasets" ? "spatialdataset" : "cmtabledata";

  const keptRelationshipIds = new Set<number>();
  const create: IAnchorRelationshipItem[] = [];

  for (const [slug, items] of Object.entries(current)) {
    for (const item of items) {
      if (item.relationship_id) {
        keptRelationshipIds.add(item.relationship_id);
      } else {
        create.push({ to_type: toAnchorType(slug), to_object_id: item.id });
      }
    }
  }

  const del: number[] = [];
  for (const items of Object.values(original)) {
    for (const item of items) {
      if (item.relationship_id && !keptRelationshipIds.has(item.relationship_id)) {
        del.push(item.relationship_id);
      }
    }
  }

  return { create, delete: del };
};

// ─── CM Table Media Relationships ────────────────────────────────────────────

/**
 * Creates a relationship between a CM table row and a media item.
 * Endpoint: POST /api/v2/relationships/
 */
export const createCmTableMediaRelationship = async (
  fromObjectId: number | string,
  toObjectId: number,
  isPrimary: boolean = false
): Promise<AxiosResponse> => {
  return api.post("v2/relationships/", {
    from_type: "cmtabledata",
    from_object_id: fromObjectId,
    to_type: "media",
    to_object_id: toObjectId,
    is_primary: isPrimary
  });
};

/**
 * Sets the primary image for a CM table row.
 * Endpoint: POST /api/v2/media/:pk/set-primary-image/
 */
export const setCmTablePrimaryImage = async (mediaId: number, cmTableDataId: number | string): Promise<AxiosResponse> => {
  return api.post(`v2/media/${mediaId}/set-primary-image/`, {
    cmtabledata: cmTableDataId
  });
};

/**
 * Creates a relationship between a Spatial Dataset and a media item.
 * Endpoint: POST /api/v2/relationships/
 */
export const createSpatialDatasetMediaRelationship = async (
  fromObjectId: number | string,
  toObjectId: number,
  isPrimary: boolean = false
): Promise<AxiosResponse> => {
  return api.post("v2/relationships/", {
    from_type: "spatialdataset",
    from_object_id: fromObjectId,
    to_type: "media",
    to_object_id: toObjectId,
    is_primary: isPrimary
  });
};

/**
 * Sets the primary image for a Spatial Dataset.
 * Endpoint: POST /api/v2/media/:pk/set-primary-image/
 */
export const setSpatialDatasetPrimaryImage = async (mediaId: number, spatialDatasetId: number | string): Promise<AxiosResponse> => {
  return api.post(`v2/media/${mediaId}/set-primary-image/`, {
    spatialdataset: spatialDatasetId
  });
};

/**
 * Deletes a v2 relationship by its relationship_id.
 * Endpoint: DELETE /api/v2/relationships/{id}/
 * Used to unlink or remove media from a CM Table row / Spatial Dataset without globally deleting the media item.
 */
export const deleteCmTableMediaRelationship = async (relationshipId: number): Promise<void> => {
  await api.delete(`v2/relationships/${relationshipId}/`);
};

/**
 * Updates fields on a CM Table row.
 * Endpoint: PATCH /api/v2/tables/{slug}/rows/{id}/
 */
export const updateCmTableRow = async (
  tableSlug: string,
  rowId: string | number,
  data: { name?: string; description?: string; geometry?: GeoJSON.Geometry; row_data?: Record<string, any>; restrictions?: number[] }
): Promise<AxiosResponse<ICmTableDataRow>> => {
  const response = await api.patch(`v2/tables/${tableSlug}/rows/${rowId}/`, data);
  if (response.data) {
    // Defensive: unwrap {message, data: {...row}} if backend wraps PATCH responses
    const rowData = response.data?.data ?? response.data;
    response.data = normaliseCmTableRow(rowData);
  }
  return response;
};

/**
 * Creates a new row in a CM Table.
 * Endpoint: POST /api/v2/tables/{slug}/rows/
 */
export const createCmTableRow = async (
  tableSlug: string,
  data: {
    name: string;
    description?: string;
    geometry?: GeoJSON.Geometry;
    row_data?: Record<string, any>;
  }
): Promise<AxiosResponse<ICmTableDataRow>> => {
  const response = await api.post(`v2/tables/${tableSlug}/rows/`, data);
  if (response.data) {
    // Backend wraps create response in {message: "Row created successfully", data: {...row}}
    const rowData = response.data?.data ?? response.data;
    response.data = normaliseCmTableRow(rowData);
  }
  return response;
};

/**
 * Deletes a row from a CM Table.
 * Endpoint: DELETE /api/v2/tables/{slug}/rows/{id}/
 */
export const deleteCmTableRow = async (tableSlug: string, rowId: string | number): Promise<AxiosResponse<void>> => {
  return api.delete(`v2/tables/${tableSlug}/rows/${rowId}/`);
};

/**
 * Updates table-level configuration (field_schema choices and/or map_display_config).
 * Endpoint: PATCH /api/v2/tables/{slug}/
 *
 * Used by the inline "add/edit styling choice" flow to register a new choice and
 * its icon/color, so the change is reflected on every feature of the table.
 */
export const updateCmTable = async (
  tableSlug: string,
  data: {
    field_schema?: Array<{ name: string; type: string; label?: string; choices?: string[] }>;
    map_display_config?: { style?: Record<string, any>; [key: string]: any };
  }
): Promise<AxiosResponse<any>> => {
  return api.patch(`v2/tables/${tableSlug}/`, data);
};

/**
 * Fetches a CM Table's definition (incl. `field_schema` + `spatial_type`).
 * Endpoint: GET /api/v2/tables/{slug}/
 */
export const getCmTable = async (
  tableSlug: string
): Promise<
  AxiosResponse<{
    slug: string;
    name: string;
    spatial_type: string;
    field_schema: Array<{ name: string; type: string; label?: string; choices?: string[] }>;
  }>
> => {
  return api.get(`v2/tables/${tableSlug}/`);
};

/**
 * Fetches GeoJSON FeatureCollection for a spatial CM Table identified by slug.
 * Supports optional bbox string "minLng,minLat,maxLng,maxLat" for viewport filtering.
 */
export const getCmTableGeo = async (
  tableSlug: string,
  params?: { search?: string; bbox?: string }
): Promise<AxiosResponse<ICmTableGeoResponse>> => {
  return api.get(`v2/tables/${tableSlug}/geojson/`, {
    params: {
      ...(params?.search ? { search: params.search } : {}),
      ...(params?.bbox ? { bbox: params.bbox } : {})
    }
  });
};

// ─── CM Table Bulk GeoJSON ─────────────────────────────────────────────────

export interface ICmTableBulkGeoResponse {
  type: "FeatureCollection";
  table_ids: number[];
  table_count: number;
  count: number;
  features: ICmTableGeoFeature[];
}

/**
 * Fetches a merged GeoJSON FeatureCollection for multiple spatial CM Tables
 * within the given viewport bounding box.
 *
 * Uses the v2 ?slugs= param (preferred over legacy ?table_ids=).
 * Each feature includes `table_id`, `table_slug`, and `spatial_type` in its
 * properties so the rendering layer can group features by table.
 */
export const getCmTableBulkGeo = async (tableSlugs: string[], bbox?: string): Promise<AxiosResponse<ICmTableBulkGeoResponse>> => {
  return api.get("v2/geojson/bulk/", {
    params: {
      slugs: tableSlugs.join(","),
      ...(bbox ? { bbox } : {})
    }
  });
};
